/**
 * Barkwoo home page components (redesign v3, bead barkwoo-6c4.12).
 *
 * <bw-home-sticky-atc>  sections/bw-home-sticky-atc.liquid
 *   - Slides in once the page is scrolled past `data-threshold` px and hides
 *     again while the footer is on screen. When hidden it is `inert`, so it
 *     never takes keyboard focus.
 *
 * <bw-scroll-row>  sections/bw-home-steps.liquid, bw-home-reviews.liquid
 *   - Wraps a list that is a grid on desktop and a scroll-snap row on phones
 *     ([data-scroll-row]). The list ships with tabindex="0", so keyboard users
 *     can scroll the phone row even before this runs (WCAG 2.1.1, axe
 *     scrollable-region-focusable). Once it runs, the tab stop stays only
 *     while the row overflows, so the desktop grid is not an empty tab stop
 *     with a focus ring (barkwoo-o0zk.59; <bw-table-scroll> does the same).
 */

if (!customElements.get('bw-home-sticky-atc')) {
  class BwHomeStickyAtc extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold) || 800;
      this.footerVisible = false;
      this.setShown(false);

      this.onScroll = () => this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });

      const footer = document.querySelector('.footer, footer, .section-footer');
      if (footer && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.footerVisible = entries.some((entry) => entry.isIntersecting);
          this.update();
        });
        this.observer.observe(footer);
      }
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
    }

    update() {
      // Keep it visible while focus is inside (e.g. the add is in flight).
      if (this.contains(document.activeElement) && this.shown) return;
      this.setShown(window.scrollY > this.threshold && !this.footerVisible);
    }

    setShown(shown) {
      if (shown === this.shown) return;
      this.shown = shown;
      this.classList.toggle('is-visible', shown);
      this.toggleAttribute('inert', !shown);
      this.setAttribute('aria-hidden', String(!shown));
      document.body.classList.toggle('bw-sticky-atc-visible', shown);
      // Read by assets/bw-home.css (scroll-padding). A class on <html>, not
      // html:has(.bw-sticky-atc.is-visible): that :has() made every style
      // recalc re-check the root and doubled recalc time on the PDP (T17).
      document.documentElement.classList.toggle('bw-sticky-atc-on', shown);
    }
  }
  customElements.define('bw-home-sticky-atc', BwHomeStickyAtc);
}

if (!customElements.get('bw-scroll-row')) {
  class BwScrollRow extends HTMLElement {
    connectedCallback() {
      this.row = this.querySelector('[data-scroll-row]');
      if (!this.row) return;
      this.onResize = () => this.update();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.row);
      } else {
        window.addEventListener('resize', this.onResize);
      }
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    update() {
      if (this.row.scrollWidth - this.row.clientWidth > 1) {
        this.row.setAttribute('tabindex', '0');
      } else {
        this.row.removeAttribute('tabindex');
      }
    }
  }
  customElements.define('bw-scroll-row', BwScrollRow);
}
